import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { ButtonGroup } from "@swa-ui/core";
import { swaDate } from "@swa-ui/date";
import { ItineraryDate } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";

import styles from "./CalendarStrip.module.scss";

const DEFAULT_NUMBER_OF_DAYS_TO_DISPLAY = 5;

/**
 * CalendarStrip shows a horizontal strip of selectable date buttons.
 */

export const CalendarStrip = (props) => {
  const {
    centerStartDate = false,
    className,
    numberOfDaysToDisplay = DEFAULT_NUMBER_OF_DAYS_TO_DISPLAY,
    onDateSelect,
    selectedStartDate,
  } = props;
  const centerDateIndex = Math.floor(numberOfDaysToDisplay / 2);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);

  useEffect(() => {
    const newIndex = getSelectedIndex();

    setSelectedDateIndex(newIndex);
  }, [selectedStartDate, centerStartDate, centerDateIndex, numberOfDaysToDisplay]);

  return <ButtonGroup {...getDateSelectionButtonGroupProps()} />;

  /**
   * Calculates the selected index based on the actual button list.
   * @returns {number} The index of the selected button
   */
  function getSelectedIndex() {
    let selectedIndex = 0;

    if (centerStartDate) {
      const today = swaDate();
      const centeredStartDate = swaDate(selectedStartDate).subtract(centerDateIndex, "day");

      if (centeredStartDate.isAfter(today) || centeredStartDate.isSame(today, "day")) {
        selectedIndex = centerDateIndex;
      } else {
        const startDate = getActualStartDate();
        const buttonList = getDateButtonList(startDate);
        const foundIndex = buttonList.findIndex((button) => button.value === selectedStartDate);

        selectedIndex = foundIndex >= 0 ? foundIndex : 0;
      }
    }

    return selectedIndex;
  }

  /**
   * Calculates the actual start date for the calendar strip and prevents past dates from being shown.
   * @returns {string} The formatted start date (YYYY-MM-DD)
   */
  function getActualStartDate() {
    const today = swaDate();
    let actualStartDate = !swaDate(selectedStartDate).isBefore(today, "day")
      ? selectedStartDate
      : today.format("YYYY-MM-DD");

    if (centerStartDate) {
      const centeredStartDate = swaDate(selectedStartDate).subtract(centerDateIndex, "day");

      actualStartDate =
        centeredStartDate.isAfter(today) || centeredStartDate.isSame(today, "day")
          ? centeredStartDate.format("YYYY-MM-DD")
          : today.format("YYYY-MM-DD");
    }

    return actualStartDate;
  }

  function getDateSelectionButtonGroupProps() {
    const startDate = getActualStartDate();
    const buttonList = getDateButtonList(startDate);
    const adjustedSelectedIndex = selectedDateIndex;

    return {
      "aria-label-next": i18n("CalendarStrip__BUTTON_GROUP_NEXT"),
      "aria-label-previous": i18n("CalendarStrip__BUTTON_GROUP_PREVIOUS"),
      buttonList,
      className,
      spaceBetween: "small",
      selectedIndex: adjustedSelectedIndex,
      size: "minimal",
    };
  }

  function getDateButtonList(startDate) {
    const startSwaDate = swaDate(startDate);

    return Array.from({ length: numberOfDaysToDisplay }, (_, index) => {
      const currentDate = startSwaDate.add(index, "day");
      const formattedDate = currentDate.format("YYYY-MM-DD");

      return {
        label: <ItineraryDate {...getTopDateProps(formattedDate)} />,
        props: {
          className: styles.dateButton,
          onClick: () => onDateSelection(index, formattedDate),
        },
        value: formattedDate,
      };
    });
  }

  function getTopDateProps(date) {
    return {
      date,
      format: "ddd,  MMM D",
      textProps: {
        size: "large",
      },
    };
  }

  function onDateSelection(dateIndex, selectedDate) {
    if (dateIndex !== selectedDateIndex) {
      setSelectedDateIndex(dateIndex);
      onDateSelect?.(selectedDate);
    }
  }
};

CalendarStrip.propTypes = {
  /** If true, renders the selectedStartDate in the center of numberOfDaysToDisplay. */
  centerStartDate: PropTypes.bool,

  /** Classes for positioning the component. */
  className: PropTypes.string,

  /** The number of days to display in the strip. */
  numberOfDaysToDisplay: PropTypes.number,

  /** Function called on clicking on a date button. Returns the formatted date */
  onDateSelect: PropTypes.func,

  /** The initial date to be selected. This should be formatted like so: 02/29/2024, 2024-2-29, etc. */
  selectedStartDate: PropTypes.string.isRequired,
};
